import { RuleBuilder, defaultRule, validateRule, type RuleDraft } from '@/components/segments/rule-builder';
import { PageHeader } from '@/components/shell/page-header';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useSegment, useUpdateSegment } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/segments/$id')({ component: SegmentDetail });

function SegmentDetail(): React.JSX.Element {
  const { id } = Route.useParams();
  const appId = useContextStore((s) => s.appId);
  const { data: segment, isLoading } = useSegment(id);
  const update = useUpdateSegment(id, appId);

  // Seeded from the query on first render that has data, then owned locally: the builder is a
  // draft the author edits, and re-syncing it to a background refetch would eat keystrokes.
  const [draft, setDraft] = useState<{ name: string; rule: RuleDraft } | null>(null);
  if (isLoading || !segment) return <Skeleton className="h-64 w-full" />;
  const state = draft ?? {
    name: segment.name,
    rule: (segment.definition ?? defaultRule()) as RuleDraft,
  };

  const error = validateRule(state.rule);

  const save = (): void => {
    if (error) return;
    update.mutate(
      { name: state.name, definition: state.rule },
      {
        onSuccess: () => toast.success('Segment saved'),
        onError: (e) => toast.error(e.message),
      }
    );
  };

  return (
    <div className="space-y-6">
      <Link
        to="/segments"
        className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ChevronLeft className="size-4" /> Audiences
      </Link>

      <PageHeader
        title={state.name || 'Untitled audience'}
        description="Define who qualifies using profile traits and product events."
        actions={
          <Button onClick={save} disabled={error !== null || state.name.trim() === '' || update.isPending}>
            Save audience
          </Button>
        }
      />

      <div className="max-w-lg space-y-2">
          <Label htmlFor="name">Name</Label>
          <Input
            id="name"
            value={state.name}
            onChange={(e) => setDraft({ ...state, name: e.target.value })}
          />
      </div>

      <div className="space-y-3 border-t pt-6">
        <div>
          <h2 className="text-sm font-semibold">Qualification rules</h2>
          <p className="text-xs text-muted-foreground">Customers must satisfy this rule tree when the campaign evaluates.</p>
        </div>
        <RuleBuilder value={state.rule} onChange={(rule) => setDraft({ ...state, rule })} />
      </div>
    </div>
  );
}
